import mustache from "./mustache.js";

let cart = null;
let checkAddToCartButtons = null;

// Lazy import checkAddToCartButtons to avoid circular dependency
async function getCheckAddToCartButtons() {
  if (!checkAddToCartButtons) {
    const uiModule = await import("./ui.js");
    checkAddToCartButtons = uiModule.checkAddToCartButtons;
  }
  return checkAddToCartButtons;
}

document.addEventListener("click", async (e) => {
  // Check if the click happened on the element with mossy-cart-action (or its children)
  const cartActionElement = e.target.closest(
    '[data-mossy-cart-action="increase"], [data-mossy-cart-action="decrease"]',
  );

  if (cartActionElement) {
    const cartItem = cartActionElement.closest('[data-mossy-item="cart-item"]'); // Get the closest .item element
    const quantityEl = cartItem.querySelector(
      '[data-mossy-cart-value="quantity"]',
    );
    const cartAction = cartActionElement.getAttribute("data-mossy-cart-action");
    let currentQty = parseInt(quantityEl.textContent, 10);

    // Get stock level from cart state
    const itemId = cartItem.id;
    const cartState = cart?.getState();
    const cartItemData = cartState?.items?.find((item) => item.id === itemId);
    const stockLevel = cartItemData?.variant?.stock_level;

    // Adjust quantity based on the action
    if (cartAction === "increase") {
      // Check stock level before increasing
      if (
        stockLevel !== null &&
        stockLevel !== undefined &&
        currentQty >= stockLevel
      ) {
        console.log(`[Mossy Cart] Cannot increase. Stock limit: ${stockLevel}`);

        // Visual feedback - flash the button
        cartActionElement.style.opacity = "0.5";
        setTimeout(() => {
          cartActionElement.style.opacity = "1";
        }, 200);

        return; // Don't proceed with increase
      }

      currentQty += 1;
      editItem(cartItem.id, currentQty);
    } else if (cartAction === "decrease") {
      if (currentQty > 1) {
        currentQty -= 1;
        editItem(cartItem.id, currentQty);
      } else {
        currentQty = 0;
        removeItem(cartItem.id);
      }
    }

    if (currentQty === 0) {
      await fetch(`/cart`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: cartItem.id,
          action: "remove",
        }),
      });
    } else {
      await fetch(`/cart`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          id: cartItem.id,
          action: cartAction,
          quantity: 1,
        }),
      });
    }
  }
});

// States Functionality
export const createState = (initialState) => {
  let state = initialState;
  const listeners = new Set();

  const getState = () => state;

  const setState = (newState) => {
    state = typeof newState === "function" ? newState(state) : newState;
    listeners.forEach((listener) => listener(state));
  };

  const subscribe = (listener) => {
    listeners.add(listener);
    return () => listeners.delete(listener);
  };

  return { getState, setState, subscribe };
};

// Initlaise Cart
export function initCart() {
  return new Promise(async (resolve) => {
    cart = createState();
    cart.subscribe(updateCartUI);

    // Expose cart globally for UI checks
    window.mossyCart = cart;

    await fetchCart();

    resolve();
  });
}

// Function to render cart items
const updateCartUI = () => {
  if (!cart.getState()) return; // Ensure cart state exists

  const cartState = cart.getState();
  const items = cartState?.items || [];

  const cartElement = document.querySelector('[data-mossy-view="cart"]');

  if (cartElement) {
    const storeData = document.querySelector(`#mossy-data-store`).innerHTML;
    const storeDetails = JSON.parse(storeData);

    const cartUI = document.querySelector(`#mossy-extracted-cart`).innerHTML;
    const cartEmptyUI = document.querySelector(
      `#mossy-extracted-empty-cart`,
    ).innerHTML;

    const updatedCartUI = mustache.render(cartUI, {
      ...cartState,
      store: storeDetails,
      tweaks: storeDetails.tweaks,
      has_multiple: function () {
        return this.quantity > 1;
      },
      price_total: function () {
        return this.variant.price * this.quantity;
      },
      price_each: function () {
        return this.variant.price;
      },
    });

    const updatedEmptyCartUI = mustache.render(cartEmptyUI, {
      ...storeDetails,
    });

    cartElement.innerHTML = items.length ? updatedCartUI : updatedEmptyCartUI;

    // Update button states after rendering
    updateCartButtonStates();
  }

  // Update Cart Values after Cart UI Displayed
  const cartTotalItems = document.querySelectorAll(
    '[data-mossy-cart-value="total-items"]',
  );

  const cartTotalPrice = document.querySelectorAll(
    '[data-mossy-cart-value="total-price"]',
  );

  if (cartTotalItems.length) {
    cartTotalItems.forEach((el) => {
      el.innerText = cartState.total?.items || 0;
    });
  }

  if (cartTotalPrice.length) {
    cartTotalPrice.forEach((el) => {
      el.innerText = cartState.total?.price || 0;
    });
  }

  // Dispatch cart update event for other components
  document.dispatchEvent(
    new CustomEvent("mossy-cart-update", {
      detail: { cart: cartState },
    }),
  );

  // Update add-to-cart button states
  getCheckAddToCartButtons().then((fn) => fn && fn());
};

// Update button states based on stock levels
const updateCartButtonStates = () => {
  const cartState = cart?.getState();
  if (!cartState) return;

  document
    .querySelectorAll('[data-mossy-item="cart-item"]')
    .forEach((cartItem) => {
      const itemId = cartItem.id;
      const cartItemData = cartState.items?.find((item) => item.id === itemId);

      if (!cartItemData) return;

      const quantityEl = cartItem.querySelector(
        '[data-mossy-cart-value="quantity"]',
      );
      const increaseBtn = cartItem.querySelector(
        '[data-mossy-cart-action="increase"]',
      );

      if (!quantityEl || !increaseBtn) return;

      const currentQty = parseInt(quantityEl.textContent) || 0;
      const stockLevel = cartItemData.variant?.stock_level;

      // Disable increase button if at stock limit
      if (
        stockLevel !== null &&
        stockLevel !== undefined &&
        currentQty >= stockLevel
      ) {
        increaseBtn.style.opacity = "0.5";
        increaseBtn.style.cursor = "not-allowed";
      } else {
        increaseBtn.style.opacity = "1";
        increaseBtn.style.cursor = "pointer";
      }
    });
};

// Fetch cart data from the server
export const fetchCart = async () => {
  try {
    const response = await fetch("/api/cart");
    if (!response.ok) throw new Error("Failed to fetch cart data");

    const cartData = await response.json();
    if (cart) {
      cart.setState(cartData); // Set entire cart data
    }
  } catch (error) {
    console.error("Error fetching cart:", error);
  }
};

// Add item to cart
export const addItem = (item) => {
  cart.setState((currentCart) => {
    let updatedCart = { ...currentCart };
    let updatedItems = [...(currentCart?.items || [])];

    // Two items are the same only if they share both variant AND options.
    // Different option selections (e.g. different personalisation text) must
    // be treated as separate line items.
    const isSameItem = (a, b) => {
      if (a.variant.id !== b.variant.id) return false;

      const aOptions = a.options ?? null;
      const bOptions = b.options ?? null;

      // Neither has options — same item
      if (!aOptions && !bOptions) return true;

      // One has options, the other doesn't — different items
      if (!aOptions || !bOptions) return false;

      // Both have options — compare serialised for a simple deep-equal
      return JSON.stringify(aOptions) === JSON.stringify(bOptions);
    };

    const existingItemIndex = updatedItems.findIndex((i) =>
      isSameItem(i, item),
    );

    if (existingItemIndex !== -1) {
      // Same variant + same options: just bump the quantity
      updatedItems[existingItemIndex] = {
        ...updatedItems[existingItemIndex],
        quantity:
          updatedItems[existingItemIndex].quantity + (item.quantity || 1),
      };
    } else {
      // New combination — add as a separate line item
      updatedItems.push({ ...item, quantity: item.quantity || 1 });
    }

    return structureCart({ cart: updatedCart, items: updatedItems });
  });
};

// Remove item from cart
export const removeItem = (id) => {
  if (!cart) return;

  cart.setState((currentCart) => {
    const updatedItems = currentCart.items.filter((item) => item.id !== id);

    return structureCart({ cart: currentCart, items: updatedItems });
  });
};

// Edit item in the cart
export const editItem = (id, quantity) => {
  if (!cart) return;

  cart.setState((currentCart) => {
    const updatedItems = currentCart.items.map((item) =>
      item.id === id
        ? {
            ...item,
            quantity,
          }
        : item,
    );

    // Return the full updated cart state
    return structureCart({ cart: currentCart, items: updatedItems });
  });
};

// HELPERS
function sumKey(itemsArray, key) {
  if (!itemsArray || itemsArray.length === 0) {
    return 0;
  }

  return itemsArray.reduce((sum, item) => {
    const value = key.split(".").reduce((obj, k) => obj && obj[k], item);
    return sum + (typeof value === "number" ? value : 0);
  }, 0);
}

function calculateItemsTotal(items) {
  return items.reduce((total, item) => {
    const itemTotal = item.variant.price * item.quantity;
    return total + itemTotal;
  }, 0);
}

function structureCart({ cart, items = [] }) {
  if (!cart) return null;

  return {
    ...cart,
    items,
    total: {
      items: sumKey(items, "quantity") ?? 0,
      price: calculateItemsTotal(items) ?? 0,
    },
  };
}
